// import { Node } from 'unist';
import { WorkspaceState, Document, Folder, DocumentActions } from './types';

/**
 * 创建文档相关操作
 * 处理文档的创建、更新、删除等操作
 * 
 * @param set 状态更新函数
 * @returns 文档操作方法集合
 */
export const createDocumentActions = (
  set: (state: WorkspaceState | ((state: WorkspaceState) => WorkspaceState)) => void
): DocumentActions => {
  return {
    /**
     * 创建新文档
     * @param title 文档标题，默认为"新文档"
     * @param folderId 所属文件夹ID，默认为"root"
     * @returns 新创建的文档ID
     */
    createDocument: (title = '新文档', folderId = 'root') => {
      const id = `doc-${Date.now()}`
      const now = Date.now()
      const newDoc: Document = {
        id,
        title,
        content: '',
        createdAt: now,
        updatedAt: now,
      }
      
      set((state: WorkspaceState) => {
        // 添加文档到文档集合
        const newDocuments = {
          ...state.documents,
          [id]: newDoc,
        }
        
        // 如果提供了文件夹 ID，将文档添加到文件夹中
        const newFolders = { ...state.folders }
        if (folderId && newFolders[folderId]) {
          newFolders[folderId] = {
            ...newFolders[folderId],
            documents: [...newFolders[folderId].documents, id],
          }
        } else {
          // 默认添加到根目录
          newFolders.root = {
            ...newFolders.root,
            documents: [...newFolders.root.documents, id],
          }
        }
        
        // 将新创建的文档添加到打开的文档列表
        const newOpenDocuments = [...state.openDocuments, id]
        
        return {
          ...state,
          documents: newDocuments,
          folders: newFolders,
          activeDocumentId: id,
          openDocuments: newOpenDocuments
        }
      })
      
      return id
    },
    
    /**
     * 更新文档内容或标题
     * @param id 文档ID
     * @param content 文档内容
     * @param title 文档标题(可选)
     * @param ast 文档AST(可选)
     */
    updateDocument: (id, content, title, ast) => {
      set((state: WorkspaceState) => {
        if (!state.documents[id]) {
          return state;
        }
        
        return {
          ...state,
          documents: {
            ...state.documents,
            [id]: {
              ...state.documents[id],
              content,
              title: title || state.documents[id].title,
              ast,
              updatedAt: Date.now(),
            },
          },
        };
      });
    },
    
    /**
     * 删除文档
     * @param id 文档ID
     */
    deleteDocument: (id) => {
      set((state: WorkspaceState) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [id]: _, ...newDocuments } = state.documents
        
        // 从所有文件夹中移除该文档
        const newFolders: Record<string, Folder> = {}
        Object.entries(state.folders).forEach(([folderId, folder]) => {
          newFolders[folderId] = {
            ...folder,
            documents: folder.documents.filter((docId: string) => docId !== id),
          }
        })
        
        // 从打开文档列表中移除
        const newOpenDocuments = state.openDocuments.filter((docId: string) => docId !== id)
        
        // 如果删除的是当前活动文档，则设置活动文档为列表中的第一个或null
        let newActiveDocumentId = state.activeDocumentId
        if (state.activeDocumentId === id) {
          newActiveDocumentId = newOpenDocuments.length > 0 ? newOpenDocuments[0] : null
        }
        
        return {
          ...state,
          documents: newDocuments,
          folders: newFolders,
          activeDocumentId: newActiveDocumentId,
          openDocuments: newOpenDocuments
        }
      })
    },
    
    /**
     * 设置活动文档
     * @param id 文档ID或null
     */
    setActiveDocument: (id) => {
      set((state: WorkspaceState) => {
        // 如果传入的id为null，只更新activeDocumentId
        if (id === null) {
          return {
            ...state,
            activeDocumentId: null
          };
        }
        
        // 如果文档不在打开列表中，将其添加到列表
        const isDocumentOpen = state.openDocuments.includes(id);
        const newOpenDocuments = isDocumentOpen 
          ? state.openDocuments 
          : [...state.openDocuments, id];
        
        return { 
          ...state,
          activeDocumentId: id,
          openDocuments: newOpenDocuments 
        };
      });
    },
    
    /**
     * 打开文档
     * @param id 文档ID
     */
    openDocument: (id) => {
      set((state: WorkspaceState) => {
        // 如果文档已经在打开列表中，只设置为活动文档
        if (state.openDocuments.includes(id)) {
          return {
            ...state,
            activeDocumentId: id
          };
        }
        
        // 否则，将文档添加到打开列表中并设置为活动文档
        return {
          ...state,
          openDocuments: [...state.openDocuments, id],
          activeDocumentId: id
        };
      });
    },
    
    /**
     * 关闭文档
     * @param id 文档ID
     */
    closeDocument: (id) => {
      set((state: WorkspaceState) => {
        // 从打开列表中移除文档
        const newOpenDocuments = state.openDocuments.filter(docId => docId !== id);
        
        // 如果关闭的是当前活动文档，则切换到列表中的下一个文档
        let newActiveDocumentId = state.activeDocumentId;
        if (state.activeDocumentId === id) {
          // 尝试找到关闭文档在列表中的位置
          const closedIndex = state.openDocuments.indexOf(id);
          if (closedIndex !== -1 && newOpenDocuments.length > 0) {
            // 如果有下一个文档，选择它，否则选择前一个或第一个
            const nextIndex = closedIndex < newOpenDocuments.length ? closedIndex : closedIndex - 1;
            newActiveDocumentId = newOpenDocuments[nextIndex >= 0 ? nextIndex : 0];
          } else {
            newActiveDocumentId = null;
          }
        }
        
        return {
          ...state,
          openDocuments: newOpenDocuments,
          activeDocumentId: newActiveDocumentId
        };
      });
    },
  };
}; 